import { DepositApplicationReadDTO } from "@/types/FinancialApplication";
import { AppToasterV2, BaseFieldProps, CoreFieldValue, hashStringSHA256 } from "@ucl/library";
import { AxiosError } from "axios";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

export const useOptIns = (financialApplicationId: string, depositApplicationId: string) => {
  const { formValue, setFormValue, saveFormData } =
    useFinancialApplicationForm(financialApplicationId);
  const [optInsValue, setOptInsValueInternal] = useState<DepositApplicationReadDTO | undefined>();
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (optInsValue) {
        await saveOptInsData(optInsValue);
      }
    },
    debounceDelay: 2000,
  });

  useEffect(() => {
    getOptIns();
  }, [financialApplicationId, depositApplicationId, formValue]);

  const getOptIns = async () => {
    if (!formValue) return;

    setIsLoading(true);
    try {
      const depositApplication = formValue.depositApplications?.find(
        (depositApp) => depositApp.id === depositApplicationId
      );

      if (depositApplication) {
        setOptInsValueInternal(depositApplication);
        const optInsHash = await hashStringSHA256(JSON.stringify(depositApplication));
        setFormValueHash(optInsHash);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setOptInsValue = async (
    value: DepositApplicationReadDTO,
    newFieldValue: CoreFieldValue,
    _fieldProps: BaseFieldProps<CoreFieldValue>
  ) => {
    if (isNil(newFieldValue) || isNil(value)) {
      return;
    }

    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (formValueHash === newHash) {
      return;
    }

    setOptInsValueInternal({ ...value });
    setFormValueHash(newHash);

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const saveOptInsData = useCallback(
    async (data: DepositApplicationReadDTO) => {
      if (!formValue) return;

      setIsSaving(true);

      try {
        // Update the deposit application in the financial application
        const updatedFormValue = {
          ...formValue,
          depositApplications: formValue.depositApplications?.map((depositApp) => {
            if (depositApp.id === depositApplicationId) {
              return data;
            }
            return depositApp;
          }),
        };

        await setFormValue(updatedFormValue, undefined);
        await saveFormData(updatedFormValue);
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving opt-ins data:", error);
        AppToasterV2.showFailedActionToaster("Failed to save opt-ins data.");
      } finally {
        setIsSaving(false);
      }
    },
    [financialApplicationId, depositApplicationId, formValue, setFormValue, saveFormData]
  );

  return {
    optInsValue,
    setOptInsValue,
    saveOptInsData,
    isSaving,
    isLoading,
  };
};
